import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft, Download, ExternalLink, Loader2, RefreshCw,
  FileText, CheckCircle2, Clock, XCircle, Search, FileSpreadsheet,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PhonePeBadge } from "../components/payments/RailSelector";
import { PayFromWalletButton } from "../components/PayFromWalletButton";

const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

const STATUS_META = {
  paid:  { label: "Paid",         tone: "text-[#2FB999] border-[#2FB999]/40 bg-[#2FB999]/[0.06]", icon: CheckCircle2 },
  sent:  { label: "Payment Due",  tone: "text-[#D8B23E] border-[#D8B23E]/40 bg-[#D8B23E]/[0.06]", icon: Clock },
  draft: { label: "Draft",        tone: "text-[#8A8A82] border-white/20 bg-white/[0.04]",        icon: Clock },
  void:  { label: "Void",         tone: "text-[#8A8A82] border-white/20 bg-white/[0.02] line-through", icon: XCircle },
};

const HEADER_LABEL = {
  trade: "Trade Invoice",
  service: "Service Invoice",
  commission: "Commission Note",
  custom: "Custom",
};

export default function AccountInvoices() {
  const { api } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/invoices/mine");
      setInvoices(res.data.invoices || []);
      setSelected(new Set());
    } catch {
      toast.error("Could not load your invoices");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const total = invoices.length;
    const paid = invoices.filter((i) => i.status === "paid");
    const due  = invoices.filter((i) => i.status !== "paid" && i.status !== "void");
    const paidAmt = paid.reduce((s, i) => s + (i.total || 0), 0);
    const dueAmt  = due.reduce((s, i) => s + (i.total || 0), 0);
    return { total, paid: paid.length, due: due.length, paidAmt, dueAmt };
  }, [invoices]);

  const filtered = useMemo(() => {
    let list = invoices;
    if (filter !== "all") list = list.filter((i) => i.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((i) =>
        (i.invoice_number || "").toLowerCase().includes(q) ||
        (i.custom_header_label || "").toLowerCase().includes(q) ||
        (i.header || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [invoices, filter, search]);

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(inv.invoice_number || inv.id).replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/invoices/mine.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vinci_invoices.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Invoice ledger exported");
    } catch {
      toast.error("Could not export CSV");
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const payable = useMemo(
    () => invoices.filter((i) => i.status !== "paid" && i.status !== "void"),
    [invoices]
  );

  const selectedInvoices = useMemo(
    () => payable.filter((i) => selected.has(i.id)),
    [payable, selected]
  );

  const selectedTotal = useMemo(
    () => selectedInvoices.reduce((s, i) => s + (i.total || 0), 0),
    [selectedInvoices]
  );

  const selectAllPayable = () => {
    if (selected.size === payable.length && payable.length > 0) setSelected(new Set());
    else setSelected(new Set(payable.map((i) => i.id)));
  };

  const bulkPay = async (railChoice = "razorpay") => {
    if (selectedInvoices.length === 0) return;
    setBulkBusy(railChoice);
    try {
      const res = await api.post("/invoices/bulk-pay", {
        invoice_ids: selectedInvoices.map((i) => i.id),
        rail: railChoice,
      });
      if (res.data?.payment_link_url) {
        if (railChoice === "phonepe") { window.location.assign(res.data.payment_link_url); return; }
        window.open(res.data.payment_link_url, "_blank", "noopener,noreferrer");
        toast.success(`Combined ₹${res.data.total.toLocaleString("en-IN")} · Razorpay opened in new tab`);
        setSelected(new Set());
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create combined payment link";
      toast.error(msg);
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] px-6 md:px-12 py-16 pb-40">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="max-w-[1400px] mx-auto">
        <Link
          to="/account"
          data-testid="invoices-back"
          className="inline-flex items-center gap-2 text-[#8A8A82] hover:text-[#B87333] text-xs uppercase tracking-[0.2em] font-grotesk mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Account
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 border-b border-white/10 pb-6">
          <div>
            <div className="overline text-[#B87333] mb-2">My Invoices</div>
            <h1 className="font-serif text-4xl md:text-5xl">Your Invoice Vault</h1>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              Every GST-compliant invoice raised on your account — download the PDF, or complete payment
              in one tap via Razorpay or PhonePe — both rails are live.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="invoices-csv-export"
              onClick={downloadCsv}
              className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-[#B87333] flex items-center gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" /> Export CSV
            </button>
            <button
              data-testid="invoices-refresh"
              onClick={load}
              className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-[#B87333] flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard testid="stat-total" label="Total Invoices" value={summary.total} />
          <StatCard testid="stat-paid" label="Paid" value={inr(summary.paidAmt)}
            subtitle={`${summary.paid} paid`} tone="text-[#2FB999]" />
          <StatCard testid="stat-due" label="Outstanding" value={inr(summary.dueAmt)}
            subtitle={`${summary.due} awaiting payment`} tone="text-[#D8B23E]" />
          <StatCard testid="stat-currency" label="Currency" value="INR"
            subtitle="All figures inclusive of applicable GST" />
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {[
              ["all", "All"],
              ["sent", "Payment Due"],
              ["paid", "Paid"],
              ["draft", "Draft"],
              ["void", "Void"],
            ].map(([k, label]) => (
              <button
                key={k}
                data-testid={`invoice-filter-${k}`}
                onClick={() => setFilter(k)}
                className={`rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.18em] font-grotesk transition-colors ${
                  filter === k
                    ? "bg-[#F5F5F0] text-black"
                    : "border border-white/20 text-[#C9C9C2] hover:border-[#B87333]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            {payable.length > 1 && (
              <button
                data-testid="invoices-select-all"
                onClick={selectAllPayable}
                className="rounded-full border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-grotesk hover:border-[#B87333] text-[#C9C9C2] shrink-0"
              >
                {selected.size === payable.length ? "Clear" : `Select ${payable.length} open`}
              </button>
            )}
            <div className="relative flex-1 md:w-72">
              <Search className="w-4 h-4 text-[#8A8A82] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="invoice-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search invoice #"
                className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-10 pr-4 py-2 text-xs text-[#F5F5F0] placeholder:text-[#8A8A82] focus:border-[#B87333] focus:outline-none font-body"
              />
            </div>
          </div>
        </div>

        {/* Invoices */}
        {loading ? (
          <div className="p-16 flex justify-center border border-white/10 rounded-2xl">
            <Loader2 className="w-6 h-6 animate-spin text-[#B87333]" />
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="invoices-empty"
            className="p-16 text-center border border-white/10 rounded-2xl"
          >
            <FileText className="w-8 h-8 text-[#8A8A82] mx-auto mb-4" />
            <div className="font-serif text-2xl mb-2">
              {invoices.length === 0 ? "No invoices yet" : "No invoices match this filter"}
            </div>
            <p className="text-sm text-[#8A8A82] font-body max-w-md mx-auto">
              {invoices.length === 0
                ? "Once a purchase, certificate or ad-hoc invoice is raised for your account, it will appear here."
                : "Try clearing the search or switching to All to see every invoice on your account."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4" data-testid="invoices-grid">
            {filtered.map((inv) => (
              <InvoiceRow
                key={inv.id}
                inv={inv}
                api={api}
                onDownload={() => downloadPdf(inv)}
                selectable={inv.status !== "paid" && inv.status !== "void"}
                checked={selected.has(inv.id)}
                onToggle={() => toggleSelect(inv.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sticky bulk-pay bar */}
      {selectedInvoices.length > 0 && (
        <div
          data-testid="bulk-pay-bar"
          className="fixed bottom-0 left-0 right-0 z-40 bg-[#050505]/95 backdrop-blur-md border-t border-[#B87333]/40 px-6 md:px-12 py-4"
        >
          <div className="max-w-[1400px] mx-auto flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#B87333] font-grotesk">
                {selectedInvoices.length} invoice{selectedInvoices.length === 1 ? "" : "s"} selected
              </div>
              <div className="font-serif text-2xl md:text-3xl mt-1" data-testid="bulk-pay-total">
                {inr(selectedTotal)}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="bulk-pay-clear"
                onClick={() => setSelected(new Set())}
                className="rounded-full border border-white/20 px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-grotesk hover:border-white/40"
              >
                Clear
              </button>
              <button
                data-testid="bulk-pay-submit"
                onClick={() => bulkPay("razorpay")}
                disabled={!!bulkBusy}
                className="rounded-full bg-[#D8B23E] text-black px-6 py-2.5 text-[10px] uppercase tracking-[0.2em] font-grotesk hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
              >
                {bulkBusy === "razorpay" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                Pay {inr(selectedTotal)} via Razorpay
              </button>
              <button
                data-testid="bulk-pay-submit-phonepe"
                onClick={() => bulkPay("phonepe")}
                disabled={!!bulkBusy}
                className="rounded-full bg-[#5F259F] text-white px-6 py-2.5 text-[10px] uppercase tracking-[0.2em] font-grotesk hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
              >
                {bulkBusy === "phonepe" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhonePeBadge size={12} />}
                Pay {inr(selectedTotal)} via PhonePe
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceRow({ inv, api, onDownload, selectable, checked, onToggle }) {
  const meta = STATUS_META[inv.status] || STATUS_META.draft;
  const Icon = meta.icon;
  const openDue = inv.status !== "paid" && inv.status !== "void";
  const razorpayHref = inv.razorpay_payment_link_url || (inv.phonepe_payment_link_url ? `${inv.phonepe_payment_link_url}?rail=razorpay` : null);
  const hasRazorpay = !!razorpayHref;
  const headerLabel = inv.custom_header_label || HEADER_LABEL[inv.header] || "Invoice";
  const [ppBusy, setPpBusy] = useState(false);
  const payPhonePe = async () => {
    setPpBusy(true);
    try {
      const r = await api.post(`/invoices/${inv.id}/phonepe-link`);
      window.location.assign(r.data.pay_url);
    } catch (e) { toast.error(e?.response?.data?.detail || "PhonePe could not open — try Razorpay"); setPpBusy(false); }
  };

  return (
    <div
      data-testid={`invoice-row-${inv.id}`}
      className={`grid grid-cols-1 md:grid-cols-[auto_1.2fr_1fr_0.8fr_1fr_1fr] gap-4 items-center border rounded-2xl px-6 py-5 bg-white/[0.02] transition-colors ${
        checked ? "border-[#B87333]/60" : "border-white/10 hover:border-[#B87333]/40"
      }`}
    >
      <div className="flex items-center">
        {selectable ? (
          <input
            type="checkbox"
            data-testid={`invoice-select-${inv.id}`}
            checked={!!checked}
            onChange={onToggle}
            className="w-4 h-4 accent-[#B87333] cursor-pointer"
            aria-label={`Select invoice ${inv.invoice_number}`}
          />
        ) : (
          <span className="w-4 h-4 inline-block" />
        )}
      </div>

      <div>
        <div className="font-mono text-sm text-[#F5F5F0]" data-testid={`invoice-number-${inv.id}`}>
          {inv.invoice_number}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mt-1">
          {headerLabel}
        </div>
      </div>

      <div>
        <div className="text-xs text-[#8A8A82] font-grotesk uppercase tracking-[0.18em]">Issued</div>
        <div className="text-sm text-[#C9C9C2] mt-1 font-body">{fmtDate(inv.issued_at)}</div>
      </div>

      <div>
        <div className="text-xs text-[#8A8A82] font-grotesk uppercase tracking-[0.18em]">Total</div>
        <div className="font-serif text-lg text-[#F5F5F0] mt-1">{inr(inv.total)}</div>
      </div>

      <div>
        <span
          className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-grotesk ${meta.tone}`}
        >
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 justify-start md:justify-end">
        <button
          onClick={onDownload}
          data-testid={`invoice-download-${inv.id}`}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-grotesk hover:border-[#B87333] hover:text-[#B87333] transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> PDF
        </button>
        {openDue && (
          <PayFromWalletButton
            invoice={{ id: inv.id, total: inv.total,
                        invoice_number: inv.invoice_number }}
            onPaid={() => window.location.reload()}
            testIdPrefix="invoice-pay-wallet"
          />
        )}
        {openDue && hasRazorpay && (
          <a
            href={razorpayHref}
            target={inv.razorpay_payment_link_url ? "_blank" : "_self"}
            rel="noopener noreferrer"
            data-testid={`invoice-pay-${inv.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#D8B23E] text-black px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-grotesk hover:brightness-110 transition-all"
          >
            Pay via Razorpay <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        {openDue && (
          <button
            type="button" onClick={payPhonePe} disabled={ppBusy}
            data-testid={`invoice-pay-phonepe-${inv.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#5F259F] text-white px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-grotesk hover:brightness-110 transition-all disabled:opacity-50"
          >
            {ppBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhonePeBadge size={12} />} Pay via PhonePe
          </button>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, subtitle, tone, testid }) {
  return (
    <div data-testid={testid} className="border border-white/10 rounded-2xl p-5 bg-white/[0.02]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-2">
        {label}
      </div>
      <div className={`font-serif text-2xl md:text-3xl ${tone || ""}`}>{value}</div>
      {subtitle ? <div className="text-[10px] text-[#8A8A82] mt-2 font-body">{subtitle}</div> : null}
    </div>
  );
}
